import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Scissors, Plus, Trash2, Star, Sparkles, Camera } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

const PRODUCT_CATEGORIES = [
  "Shampoo", "Conditioner", "Finishing spray",
  "Detangler", "Tool", "Cologne", "Other",
];

const TRIM_HINTS = [
  "Show trim", "Continental", "Puppy clip", "Pet trim",
  "Strip", "Hand-stripped", "Bath & tidy",
];

const TOP_RATING_THRESHOLD = 4;   // products with rating ≥ this are "top"

// Sort by rating desc, then most-recently-created, cap at `limit`.
function pickTopProducts(products, limit = 4) {
  return [...(products || [])]
    .filter((p) => (p.rating || 0) >= TOP_RATING_THRESHOLD)
    .sort((a, b) => (b.rating || 0) - (a.rating || 0) || ((b.created_at || "") > (a.created_at || "") ? 1 : -1))
    .slice(0, limit);
}

/**
 * Grooming log + product library, dog-scoped. Two sub-cards inside one
 * section so the user thinks of them as related but edits them at different
 * cadences (sessions every visit, products rarely). The products list is
 * lifted here so the sessions card can show "top products" quick-picks.
 */
export default function GroomingSection({ dog }) {
  const [products, setProducts] = useState([]);

  const loadProducts = async () => {
    try {
      const { data } = await api.get("/grooming/products", { params: { dog_id: dog.id } });
      setProducts(data.items || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.debug("grooming products load failed", err);
    }
  };

  useEffect(() => { loadProducts(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dog.id]);

  const topProducts = pickTopProducts(products);

  return (
    <div data-testid="grooming-section">
      <div className="flex items-center gap-2 mb-4">
        <Scissors size={18} className="text-primary" />
        <h3 className="font-display text-xl font-bold tracking-tight">Grooming</h3>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SessionsCard dog={dog} topProducts={topProducts} />
        <ProductsCard dog={dog} products={products} onChanged={loadProducts} />
      </div>
    </div>
  );
}

// ----- Sessions ------------------------------------------------------------

function blankSession(dogId) {
  return {
    dog_id: dogId,
    session_date: new Date().toISOString().slice(0, 10),
    trim_type: "",
    blade_length: "",
    scissors_used: "",
    products_used: "",
    notes: "",
    before_photo_url: "",
    after_photo_url: "",
  };
}

// Downsize an image File to a manageable data URL (max 900px wide, JPEG q=0.78).
// Keeps Mongo documents small enough that session lists load fast.
function fileToDataUrl(file, maxWidth = 900) {
  return new Promise((resolve, reject) => {
    if (!file) return resolve("");
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.78));
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function SessionsCard({ dog, topProducts }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blankSession(dog.id));
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/grooming/sessions", { params: { dog_id: dog.id } });
      setItems(data.items || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.debug("grooming sessions load failed", err);
    }
  };

  useEffect(() => { load(); setForm(blankSession(dog.id)); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dog.id]);

  const appendTopProduct = (name) => {
    setForm((f) => {
      const current = (f.products_used || "").trim();
      if (current.toLowerCase().includes(name.toLowerCase())) return f;     // already added
      return { ...f, products_used: current ? `${current}, ${name}` : name };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.session_date) return;
    setSaving(true);
    try {
      await api.post("/grooming/sessions", form);
      toast.success("Grooming session logged");
      setForm(blankSession(dog.id));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this grooming session?")) return;
    try {
      await api.delete(`/grooming/sessions/${id}`);
      load();
    } catch (err) { toast.error("Could not delete"); }
  };

  return (
    <Card className="border-border shadow-none rounded-lg" data-testid="grooming-sessions-card">
      <CardContent className="p-5 sm:p-6">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Log</p>
        <p className="font-display text-lg font-bold tracking-tight mt-0.5 mb-4">Recent grooming sessions</p>

        <form onSubmit={submit} className="space-y-3 mb-5" data-testid="grooming-session-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Date</Label>
              <Input type="date" value={form.session_date} onChange={(e) => setForm({ ...form, session_date: e.target.value })} className="mt-1.5" required data-testid="grooming-date" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Trim type</Label>
              <Input value={form.trim_type} onChange={(e) => setForm({ ...form, trim_type: e.target.value })} placeholder="e.g. Continental" list="grooming-trim-hints" className="mt-1.5" data-testid="grooming-trim" />
              <datalist id="grooming-trim-hints">
                {TRIM_HINTS.map((t) => <option key={t} value={t} />)}
              </datalist>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Blade length</Label>
              <Input value={form.blade_length} onChange={(e) => setForm({ ...form, blade_length: e.target.value })} placeholder="e.g. #10 body, #7F top-knot" className="mt-1.5" data-testid="grooming-blade" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Scissors used</Label>
              <Input value={form.scissors_used} onChange={(e) => setForm({ ...form, scissors_used: e.target.value })} placeholder={'e.g. 7" straight, 8" curved'} className="mt-1.5" data-testid="grooming-scissors" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Products used (this session)</Label>
            {topProducts && topProducts.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid="grooming-top-products-chips">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Top picks:</span>
                {topProducts.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => appendTopProduct(p.name)}
                    className="px-2 py-0.5 rounded-full border border-primary/30 bg-primary/5 hover:bg-primary/10 text-xs font-medium text-primary transition-colors"
                    data-testid={`grooming-top-pick-${p.id}`}
                    title={`Add ${p.name} to this session`}
                  >
                    + {p.name}
                  </button>
                ))}
              </div>
            )}
            <Input value={form.products_used} onChange={(e) => setForm({ ...form, products_used: e.target.value })} placeholder="e.g. Plush Puppy + Chris Christensen Ice" className="mt-1.5" data-testid="grooming-products-used" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Coat condition, time taken, what to do differently next time" className="mt-1.5" data-testid="grooming-notes" />
          </div>

          {/* Photo diary — before/after (optional). Client-side downsize keeps
              the doc small; stored as base64 in Mongo alongside the session. */}
          <div className="grid grid-cols-2 gap-3" data-testid="grooming-photo-fields">
            {[
              { key: "before_photo_url", label: "Before photo" },
              { key: "after_photo_url", label: "After photo" },
            ].map((slot) => (
              <div key={slot.key}>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">{slot.label}</Label>
                <div className="mt-1.5 flex items-center gap-2">
                  {form[slot.key] ? (
                   
                    <img src={form[slot.key]} alt={slot.label} className="h-16 w-16 object-cover rounded-md border border-border" />
                  ) : (
                    <div className="h-16 w-16 rounded-md border border-dashed border-border grid place-items-center text-muted-foreground">
                      <Camera size={16} />
                    </div>
                  )}
                  <div className="flex-1 flex flex-col gap-1">
                    <input
                      id={`photo-${slot.key}`}
                      type="file"
                      accept="image/*"
                      className="text-xs"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const url = await fileToDataUrl(file);
                          setForm((f) => ({ ...f, [slot.key]: url }));
                        } catch { toast.error("Could not read that image"); }
                      }}
                      data-testid={`grooming-${slot.key}-input`}
                    />
                    {form[slot.key] ? (
                      <button
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, [slot.key]: "" }))}
                        className="text-[11px] text-destructive hover:underline text-left"
                        data-testid={`grooming-${slot.key}-clear`}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="grooming-session-save">
            <Plus size={14} className="mr-1.5" /> {saving ? "Saving…" : "Log session"}
          </Button>
        </form>

        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="grooming-sessions-empty">No sessions logged yet.</p>
        ) : (
          <ul className="divide-y divide-border border border-border rounded-md" data-testid="grooming-sessions-list">
            {items.map((s) => (
              <li key={s.id} className="p-3 flex items-start justify-between gap-3" data-testid={`grooming-session-${s.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {s.session_date ? format(parseISO(s.session_date), "MMM d, yyyy") : "—"}
                    {s.trim_type ? ` · ${s.trim_type}` : ""}
                  </p>
                  {(s.blade_length || s.scissors_used) && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {s.blade_length ? `Blade: ${s.blade_length}` : ""}
                      {s.blade_length && s.scissors_used ? " · " : ""}
                      {s.scissors_used ? `Scissors: ${s.scissors_used}` : ""}
                    </p>
                  )}
                  {s.products_used && <p className="text-xs text-muted-foreground mt-1">Products: {s.products_used}</p>}
                  {s.notes && <p className="text-xs text-foreground/70 mt-1 whitespace-pre-wrap">{s.notes}</p>}
                  {(s.before_photo_url || s.after_photo_url) ? (
                    <div className="flex items-center gap-2 mt-2" data-testid={`grooming-session-photos-${s.id}`}>
                      {s.before_photo_url ? (
                        <a href={s.before_photo_url} target="_blank" rel="noopener noreferrer" className="group relative">
                          
                          <img src={s.before_photo_url} alt="Before" className="h-16 w-16 object-cover rounded-md border border-border group-hover:opacity-90" />
                          <span className="absolute bottom-0 left-0 right-0 text-[9px] uppercase tracking-wider text-white bg-black/50 px-1 text-center rounded-b-md">Before</span>
                        </a>
                      ) : null}
                      {s.after_photo_url ? (
                        <a href={s.after_photo_url} target="_blank" rel="noopener noreferrer" className="group relative">
                          
                          <img src={s.after_photo_url} alt="After" className="h-16 w-16 object-cover rounded-md border border-border group-hover:opacity-90" />
                          <span className="absolute bottom-0 left-0 right-0 text-[9px] uppercase tracking-wider text-white bg-black/50 px-1 text-center rounded-b-md">After</span>
                        </a>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <button onClick={() => remove(s.id)} className="p-1.5 hover:bg-muted rounded-md text-muted-foreground hover:text-destructive shrink-0" data-testid={`grooming-session-delete-${s.id}`} aria-label="Delete session">
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ----- Products ------------------------------------------------------------

function blankProduct(dogId) {
  return { dog_id: dogId, name: "", brand: "", category: "Shampoo", notes: "", rating: 0 };
}

function ProductsCard({ dog, products, onChanged }) {
  const [form, setForm] = useState(blankProduct(dog.id));
  const [saving, setSaving] = useState(false);
  const items = products || [];

  useEffect(() => { setForm(blankProduct(dog.id)); }, [dog.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.post("/grooming/products", form);
      toast.success("Product added");
      setForm(blankProduct(dog.id));
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this product?")) return;
    try {
      await api.delete(`/grooming/products/${id}`);
      onChanged?.();
    } catch (err) { toast.error("Could not delete"); }
  };

  return (
    <Card className="border-border shadow-none rounded-lg" data-testid="grooming-products-card">
      <CardContent className="p-5 sm:p-6">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Library</p>
        <p className="font-display text-lg font-bold tracking-tight mt-0.5 mb-4">Favorite products</p>

        <form onSubmit={submit} className="space-y-3 mb-5" data-testid="grooming-product-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Product name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" placeholder="e.g. Ice on Ice" data-testid="grooming-product-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Brand</Label>
              <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="e.g. Chris Christensen" className="mt-1.5" data-testid="grooming-product-brand" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Category</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5 w-full border border-input rounded-md bg-background px-3 py-2 text-sm h-10" data-testid="grooming-product-category">
                {PRODUCT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Rating</Label>
              <RatingPicker value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} testid="grooming-product-rating" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="What it's best for, dilution ratio, where you buy it" className="mt-1.5" data-testid="grooming-product-notes" />
          </div>
          <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="grooming-product-save">
            <Plus size={14} className="mr-1.5" /> {saving ? "Saving…" : "Add product"}
          </Button>
        </form>

        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="grooming-products-empty">No products yet.</p>
        ) : (
          <ul className="divide-y divide-border border border-border rounded-md" data-testid="grooming-products-list">
            {items.map((p) => (
              <li key={p.id} className="p-3 flex items-start justify-between gap-3" data-testid={`grooming-product-${p.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium truncate">
                      {p.name}{p.brand ? <span className="text-muted-foreground"> · {p.brand}</span> : null}
                    </p>
                    {(p.rating || 0) >= TOP_RATING_THRESHOLD && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[9px] uppercase tracking-wider font-semibold border border-primary/30" data-testid={`grooming-product-top-${p.id}`} title="Top pick — surfaced as a quick add on the session form">
                        <Sparkles size={9} /> Top
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {p.category || "Other"}{p.rating ? ` · ${"★".repeat(p.rating)}${"☆".repeat(5 - p.rating)}` : ""}
                  </p>
                  {p.notes && <p className="text-xs text-foreground/70 mt-1 whitespace-pre-wrap">{p.notes}</p>}
                </div>
                <button onClick={() => remove(p.id)} className="p-1.5 hover:bg-muted rounded-md text-muted-foreground hover:text-destructive shrink-0" data-testid={`grooming-product-delete-${p.id}`} aria-label="Delete product">
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function RatingPicker({ value, onChange, testid }) {
  return (
    <div className="mt-1.5 flex items-center gap-1 h-10" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n === value ? 0 : n)}
          aria-label={`${n} star${n === 1 ? "" : "s"}`}
          className="p-1 text-muted-foreground hover:text-primary transition-colors"
          data-testid={`${testid}-star-${n}`}
        >
          <Star size={18} className={n <= value ? "fill-primary text-primary" : ""} />
        </button>
      ))}
    </div>
  );
}
